import { TranslateService } from '@ngx-translate/core';
import { LinkedMember } from './../../member/model/member';
import { UserService } from 'src/app/core/service/user.service';
import { Component, DestroyRef, OnInit, Input, ViewEncapsulation, ViewChild, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SettingService } from 'src/app/core/service/setting.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { Router } from '@angular/router';
import { MemberService } from 'src/app/core/service/member.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { NGXLogger } from 'ngx-logger';
import { RegistrationService } from 'src/app/core/service/registration.service';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import { IntlService } from '@progress/kendo-angular-intl';
import moment from 'moment';
import { PatientEligibilityComponent } from '../patient-eligibility/patient-eligibility.component';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { ConsentService } from 'src/app/core/service/consent.service';

@Component({
  standalone: false,
  selector: 'app-patient-header',
  templateUrl: './patient-header.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./patient-header.component.scss'],
  styles: [`
  #track-patient.k-checkbox:checked {
    border-color: #285550;
    background-color: #285550;
  }
  `]
})
export class PatientHeaderComponent implements OnInit
{
  @ViewChild('patientEligibility') public patientEligibility: PatientEligibilityComponent;
  @ViewChildren(TooltipDirective) public tooltipDir: QueryList<TooltipDirective>;

  @Input() public showHeader: boolean;
  @Input() public type: string;

  public patientLabel = {};
  public canEditPatient = false;
  public editPatientDialog = false;
  public communityEnabled = false;
  public communityPortalEnabled = false;
  public communityConsentEnabled = false;
  public linkedMembers: LinkedMember[] = [];
  public defaultProxyItem: any;
  public consentAllowed: boolean;
  public patientRestricted: boolean;
  public patientOptedOut: boolean;
  public patientOptedOutAndRestricted: boolean;
  public compositeRiskScore = 0;
  public headerCollapsed = false;
  public openSendMethodDialog = false;
  public sendMethodType = '';
  public textBY = '';
  public memberSelected: any = null;
  public openPatientConsentTab = false;
  public latestMemberConsent: string;
  public consentExpired: boolean;
  public consentColor: any;
  public consentExpirationPeriod: string;
  public insurancesText: string;
  public enhancedServicesText: string;
  public openClientSearchDialog = false;
  public refreshPatientHouseholdSection: boolean = false;
  public isPartOfAHousehold: boolean = false;
  public selectedMemberId: number;
  public selectedMemberName = '';
  public deleteDialogOpened = false;
  public eligibilityAPIEnabled = false;
  public memberConsentStatus: any = null;
  public windowState = 'maximized';
  public isConfigurableHeader: boolean;

  private loadedMember: any;

  constructor(
    private consentService: ConsentService,
    private settingService: SettingService,
    private userService: UserService,
    private authenticationService: AuthenticationService,
    private memberService: MemberService,
    private translate: TranslateService,
    private messageHandlerService: MessageHandlerService,
    private logger: NGXLogger,
    private router: Router,
    private registrationService: RegistrationService,
    private messengerService: MessengerService,
    public intl: IntlService,
    private readonly destroyRef: DestroyRef) { }

  public get member(): any
  {
    return this.loadedMember;
  }

  @Input() public set member(value: any)
  {
    this.loadedMember = value;
    this.getMemberCommunityInfo();
    this.loadLinkedMembers();
    if (value !== undefined && value && value?.memberLink.length === 0)
    {
      this.isMemberPartOfAHousehold();
    }
    this.setInsurancesText();
    this.setEnhancedServicesText();
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (sessionSetting) =>
      {
        this.isConfigurableHeader = (sessionSetting.clientHeaderSection1 != '' || sessionSetting.clientHeaderSection2 != '' || sessionSetting.clientHeaderSection3 != '');
        //this.isConfigurableHeader = false;    // Uncomment this to disable and none configurable header
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.communityEnabled = sessionSetting.communityEnabled;
        this.communityPortalEnabled = sessionSetting.communityPortalEnabled;
        this.communityConsentEnabled = sessionSetting.communityConsentEnabled;
        this.eligibilityAPIEnabled = sessionSetting.eligibilityAPIEnabled || this.memberService.eligibilitySimulationMode() != null;

        // Enable Patient Edit Demographics button
        if (sessionSetting.patientEditRoles !== '')
        {
          const currentUser = this.authenticationService.currentUser;
          this.canEditPatient = this.userService.isRoleInList(currentUser.role, sessionSetting.patientEditRoles);
        }
        else
        {
          this.canEditPatient = false;
        }
      }
    });

    this.messengerService.receiveMessage().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (message) =>
      {
        if (message.type === 'openEditCareTeamMember' && message.data)
        {
          this.editPatientDialog = false;
          this.openClientSearchDialog = false;
          this.messengerService.sendMessage(new MessageSubject('openEditCareTeamMemberPatientSummary', message.data));
          this.messengerService.sendMessage(new MessageSubject('openEditCareTeamMemberCaseDetails', message.data));
        }
        else if (message.type === 'refreshMemberConsent')
        {
          this.refreshMemberConsent(this.loadedMember.id);
        }
        else if (message.type === 'openVideo')
        {
          this.openVideo(message.data.url);
        }
        else if (message.type === 'refreshPatientHeader')
        {
          // get Member by ID from REST service
          this.memberService.getMember(this.loadedMember.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (data) =>
            {
              this.loadedMember = data;
              this.setInsurancesText();
              this.setEnhancedServicesText();
            },
            error: (errorResponse: any) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
        else if (message.type === 'refreshPatientHouseholdSection')
        {
          this.refreshPatientHouseholdSection = true;
        }
      }
    });

    this.translate.get(['SELECT-PROXY-MEMBER', 'BY']).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) =>
      {
        this.defaultProxyItem = { fullName: res['SELECT-PROXY-MEMBER'], memberId: null };
        this.textBY = res.BY.toLowerCase();
      }
    });
  }

  public getPovertyText(familyIncome: number, povertyLevel: number): string 
  {
    return this.memberService.getPovertyText(familyIncome, povertyLevel);
  }

  public getSpokenLanguagesText(): string
  {
    return this.member?.spokenLanguages !== null ? this.member?.spokenLanguages.map(x => x.displayName).join(', ') : '';
  }

  public editPatient(): void
  {
    this.editPatientDialog = true;
    this.openPatientConsentTab = false;
  }

  public editPatientConsent(): void
  {
    this.editPatientDialog = true;
    this.openPatientConsentTab = true;
  }

  public afterSavePatient(member: any): void
  {
    this.editPatientDialog = false;
    this.member = member;
    this.messengerService.sendMessage(new MessageSubject('refreshSummaryCounts'));
  }

  public afterApplyPatient(member: any): void
  {
    this.member = member;
    this.messengerService.sendMessage(new MessageSubject('refreshSummaryCounts'));
  }

  public checkMedicaidEligibility(): void
  {
    /*PAYER-5664: Simulate Lock issue and call checkMemberEligibility repeatedly. Keep in code for futher testing if needed.
    setInterval(() =>
    {
      this.memberService.checkMemberEligibility(this.member.id).subscribe({ error: (errorMessage) => { this.messageHandlerService.displayError(errorMessage); } });
    }, 1000);*/
    this.patientEligibility.execute(this.member.id);
  }

  /**
   * This method navigate to a specific Member Id
   *
   * @param memberId Member Id to navigate to.
   */
  public openMember(memberId: number): void
  {
    this.router.navigate([`/patient/${memberId}`]);
  }

  public isConsentLinkAllowed(consentAllowed: boolean): boolean
  {
    return !this.communityConsentEnabled || consentAllowed;
  }

  public openVideo(url: string): void
  {
    const win = window.open(url, '_blank');
    win.focus();
  }

  /**
  * Change the person from the linked member drop-down (proxy)
  *
  * @param memberId Member id to switch to.
  */
  public onLinkedMemberChanged(memberId: number): void
  {
    if (memberId !== null)
    {
      // This is a work around to force url to change and reload. Found no other better solution yet.
      const patientUrl = this.router.url.includes('patient-proxy') ? '/patient' : '/patient-proxy';
      this.router.navigate([`${patientUrl}/${memberId}`]);
    }
  }

  /**
   * Get the member photo if not null, otherwise returns the no-photo.
   *
   * @param photo Current member photo.
   * @returns string Photo url to show.
   */
  public getImageUrl(photo: any): string
  {
    return photo ? `data:image/jpeg;base64,${photo}` : 'assets/images/no-photo.jpg';
  }

  public setTrackMember(track: boolean): void
  {
    this.memberService[track ? 'trackMember' : 'untrackMember'](this.member.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () =>
      {
        this.member.starred = track;
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
        this.logger.error(error);
      }
    });
  }

  public openMethodDialog(member: any, type: string): void
  {
    this.sendMethodType = type;
    this.memberSelected = member;
    const noEmail = !(this.memberSelected.email !== null && this.memberSelected.email !== '');

    if (noEmail && type === 'INVITE')
    {
      this.messageHandlerService.displayError('COMMUNITY-INVITE-EMAIL-REQUIRED');
    }
    else
    {
      if (type === 'INVITE')
      {
        this.registrationService.emailExists(this.memberSelected.email).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (response) =>
          {
            if (response)
            {
              this.messageHandlerService.displayError('EMAIL-ALREADY-REGISTERED');
            }
            else
            {
              this.openSendMethodDialog = true;
            }
          }
        });
      }
      else
      {
        this.openSendMethodDialog = true;
      }
    }
  }

  public closeSendMethod(refreshList: boolean): void
  {
    if (refreshList)
    {
      this.getMemberCommunityInfo();
    }
    this.openSendMethodDialog = false;
  }

  public getInviteFromOrganizationName(member: any): string
  {
    if (member && member.inviteOrganizationName)
    {
      return `${this.textBY} ${member.inviteOrganizationName}`;
    }
    else
    {
      return '';
    }
  }

  public mergedPatient(): boolean
  {
    if (this.member !== null && this.member !== undefined && this.member.memberMergeHistory !== null)
    {
      return true;
    }
    else
    {
      return false;
    }
  }

  public consentExpirationColor()
  {
    if (this.latestMemberConsent !== null && this.latestMemberConsent !== undefined)
    {
      const now = new Date();
      const latestMemberConsent = new Date(this.latestMemberConsent);
      const d1 = moment(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
      const d2 = moment(Date.UTC(latestMemberConsent.getFullYear(), latestMemberConsent.getMonth(), latestMemberConsent.getDate()));
      const difference = d2.diff(d1, 'months');
      this.translate.get(['CONSENT-EXPIRATION-UNDER3', 'CONSENT-EXPIRATION-36', 'CONSENT-EXPIRATION-OVER6']).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res) =>
        {
          if (this.memberConsentStatus !== null)
          {
            if (this.memberConsentStatus === 'PENDING')
            {
              this.consentColor = this.memberService.memberConsentOrange;
            }
            else
              if (this.memberConsentStatus === 'DECLINED')
              {
                this.consentColor = this.memberService.memberConsentRed;
              }
          }
          else
          {
            if (difference > 6)
            {
              this.consentColor = this.memberService.memberConsentGreen;
              this.consentExpirationPeriod = res['CONSENT-EXPIRATION-OVER6'];
            }
            else if (difference < 3)
            {
              this.consentColor = this.memberService.memberConsentRed;
              this.consentExpirationPeriod = res['CONSENT-EXPIRATION-UNDER3'];
            }
            else
            {
              this.consentColor = this.memberService.memberConsentOrange;
              this.consentExpirationPeriod = res['CONSENT-EXPIRATION-36'];
            }
          }
        }
      });
    }
  }

  public openClientSearch(): void
  {
    this.openClientSearchDialog = true;
  }

  public closeAddHousehold(): void
  {
    if (this.refreshPatientHouseholdSection)
    {
      this.loadLinkedMembers();
    }
    this.openClientSearchDialog = false;
  }

  public unlinkMember(): void
  {
    this.memberService.removeHouseholdMember(this.selectedMemberId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('HOUSEHOLD-MEMBER-REMOVED-SUCCESSFULLY');
        this.loadLinkedMembers();
      },
      error: (error) =>
      {
        this.messageHandlerService.displayError(error);
        this.logger.error(error);
      }
    });
    this.deleteDialogOpened = false;
  }

  public deleteHandler(e: any): void
  {
    this.selectedMemberId = e.value.memberId;
    this.selectedMemberName = e.value.fullName;
    this.deleteDialogOpened = true;
  }

  public showTooltip(e: ElementRef, tooltip: ElementRef): void
  {
    const tooltipDirective = this.tooltipDir.toArray().find(t => t.tooltipWrapper.nativeElement === tooltip);
    if (tooltipDirective)
    {
      tooltipDirective.toggle(e, true); // Show the tooltip
    }
  }

  public hideTooltip(tooltip: ElementRef): void
  {
    const tooltipDirective = this.tooltipDir.toArray().find(t => t.tooltipWrapper.nativeElement === tooltip);
    if (tooltipDirective)
    {
      tooltipDirective.hide(); // Show the tooltip
    }
  }

  public getOptOutDateTitle(optOutDate: string, optOutReason: string): string
  {
    return this.consentService.getOptOutDateTitle(optOutDate, optOutReason);
  }

  private getMemberCommunityInfo(): void
  {
    this.consentAllowed = true;
    this.patientRestricted = false;
    this.patientOptedOut = false;
    this.patientOptedOutAndRestricted = false;
    this.memberSelected = null;
    this.latestMemberConsent = null;
    this.consentExpired = null;

    if (this.loadedMember && this.loadedMember.id > 0)
    {
      if (this.communityEnabled)
      {
        this.memberService.getMemberInviteInfo(this.loadedMember.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (memberData: any) =>
          {
            if (memberData && memberData.total === 1)
            {
              this.memberSelected = memberData.data[0];
            }
          }
        });
        this.refreshMemberConsent(this.loadedMember.id);
        this.getMemberRisks();
      }
    }
  }

  private getMemberRisks(): void
  {
    this.memberService.getMemberRisks(this.loadedMember.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (risks) =>
      {
        let count = 0;
        risks.forEach(d =>
        {
          if (d.score !== null && d.score > 0)
          {
            this.compositeRiskScore += d.score;
            count++;
          }
        });
        if (count > 0)
        {
          this.compositeRiskScore = Math.round(this.compositeRiskScore / count * 10) / 10;
        }
      }
    });
  }

  private isMemberPartOfAHousehold(): void
  {
    if (this.loadedMember !== undefined && (this.type === 'patient' || this.type === 'case') && this.member !== null && this.member.id > 0)
    {
      this.memberService.isMemberPartOfAHousehold(this.member.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (response) =>
        {
          this.isPartOfAHousehold = (response !== null);
        }
      });
    }
  }

  private loadLinkedMembers(): void
  {
    this.linkedMembers = [];
    if (this.loadedMember !== undefined && (this.type === 'patient' || this.type === 'case') && this.member !== null && this.member.id > 0)
    {
      this.memberService.getLinkedMember(this.member.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (linkedMembers) =>
        {
          this.linkedMembers = linkedMembers;
        }
      });
    }
  }

  /**
   * Set the metadata for the Member Consent icon
   *
   * @param memberId Current loaded member id
   */
  private refreshMemberConsent(memberId: number): void
  {
    this.memberService.getMemberConsentInfo(memberId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (consentData: any) =>
      {
        this.consentAllowed = consentData.consentAllowed;
        this.patientRestricted = consentData.patientRestricted;
        this.patientOptedOut = consentData.patientOptedOut;
        this.patientOptedOutAndRestricted = consentData.patientOptedOutAndRestricted;
        this.latestMemberConsent = consentData.latestMemberConsent;
        this.consentExpired = consentData.consentExpired;
        this.memberConsentStatus = consentData.memberConsentStatus;
        this.consentExpirationColor();
      }
    });
  }

  private setInsurancesText(): void
  {
    this.insurancesText = '';
    if (this.loadedMember)
    {
      const insurers = this.loadedMember.insurances.map((i: any) => (i.insurer.name));
      this.insurancesText = insurers.sort().join(', ');
    }
  }

  private setEnhancedServicesText(): void
  {
    this.enhancedServicesText = '';
    if (this.loadedMember)
    {
      const services = this.loadedMember.enhancedServices.map((i: any) => (i.displayName));
      this.enhancedServicesText = services.sort().join(', ');
    }
  }

  private getMemberMedicaidNumber(): any
  {
    return this.member.identifiers.find((x: any) => x.dictionary.code === 'MA');
  }
}
